import { gql } from "@apollo/client";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { DashboardDataContext } from "./DashboardDataContext";
import QueryExecutor from "./QueryExecutor";

const parseQueryDocument = (queryString) => {
  try {
    return { document: gql(queryString), error: null };
  } catch (e) {
    return { document: null, error: e.message };
  }
};

const DashboardDataProvider = ({ dashboard, children }) => {
  const [activeTab, setActiveTab] = useState("template");
  const [isEditing, setIsEditing] = useState(false);
  const [localCss, setLocalCss] = useState(dashboard.css);
  const [localTemplate, setLocalTemplate] = useState(dashboard.template);
  const [localQuery, setLocalQuery] = useState(dashboard.queries);
  const [queryState, setQueryState] = useState({
    queryData: {},
    loading: true,
    error: null,
  });

  const effectiveCss = isEditing ? localCss : dashboard.css;
  const effectiveTemplate = isEditing ? localTemplate : dashboard.template;
  const effectiveQuery = isEditing ? localQuery : dashboard.queries;

  const parsed = useMemo(
    () => parseQueryDocument(effectiveQuery),
    [effectiveQuery]
  );

  const [debouncedDocument, setDebouncedDocument] = useState(parsed.document);

  useEffect(() => {
    if (!isEditing) {
      setDebouncedDocument(parsed.document);
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedDocument(parsed.document);
    }, 500);
    return () => clearTimeout(timer);
  }, [parsed.document, isEditing]);

  const toggleEdit = useCallback(() => {
    if (!isEditing) {
      setLocalCss(dashboard.css);
      setLocalTemplate(dashboard.template);
      setLocalQuery(dashboard.queries);
    }
    setIsEditing((prev) => !prev);
  }, [isEditing, dashboard]);

  const reset = useCallback(() => {
    setLocalCss(dashboard.css);
    setLocalTemplate(dashboard.template);
    setLocalQuery(dashboard.queries);
  }, [dashboard]);

  const contextValue = useMemo(
    () => ({
      queryData: queryState.queryData,
      loading: queryState.loading,
      error: queryState.error,
      parseError: parsed.error,
      effectiveCss,
      effectiveTemplate,
      isEditing,
      localCss,
      localTemplate,
      localQuery,
      toggleEdit,
      reset,
      setLocalCss,
      setLocalTemplate,
      setLocalQuery,
      activeTab,
      setActiveTab,
    }),
    [
      queryState,
      parsed.error,
      effectiveCss,
      effectiveTemplate,
      isEditing,
      localCss,
      localTemplate,
      localQuery,
      toggleEdit,
      reset,
      activeTab,
    ]
  );

  return (
    <DashboardDataContext.Provider value={contextValue}>
      <QueryExecutor
        document={debouncedDocument}
        onQueryState={setQueryState}
      />
      {children}
    </DashboardDataContext.Provider>
  );
};

DashboardDataProvider.propTypes = {
  dashboard: PropTypes.shape({
    css: PropTypes.string.isRequired,
    queries: PropTypes.string.isRequired,
    template: PropTypes.string.isRequired,
  }).isRequired,
  children: PropTypes.node.isRequired,
};

export default DashboardDataProvider;
